import { SwissParties } from 'Constants/SwissParties'
import * as d3 from 'd3'
import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useElementSize } from 'usehooks-ts'
import { appendHistogramm } from './ChartUtils'
import { ToolTip } from 'components/ToolTip/ToolTip'
import leftArrow from 'Assets/icon-left-arrow.svg'
import rightArrow from 'Assets/icon-right-arrow.svg'

const MainChart = ({
  data,
  categoryToDisplay,
  xScaleExtent,
  showSinglePoints,
  partyFilter,
  highlightCouncilMembers,
}: {
  data: NationalCouncilLegislature
  categoryToDisplay: keyof CouncilMember
  xScaleExtent: [number, number]
  showSinglePoints?: boolean
  partyFilter?: string[]
  highlightCouncilMembers?: string[]
}) => {
  const svgRef = useRef<SVGSVGElement>(null)
  const [svgSelection, setSvgSelection] =
    useState<d3.Selection<SVGSVGElement | null, unknown, null, undefined>>()
  const [xAxisSelection, setXAxisSelection] =
    useState<d3.Selection<SVGGElement, unknown, null, undefined>>()
  const [chartGroupSelection, setChartGroupSelection] =
    useState<d3.Selection<SVGGElement, unknown, null, undefined>>()
  const [histogramGroupSelection, setHistogramGroupSelection] =
    useState<d3.Selection<SVGGElement, unknown, null, undefined>>()
  const [xLinearScale, setXLinearScale] =
    useState<d3.ScaleLinear<number, number>>()
  const [yLinearScale, setYLinearScale] =
    useState<d3.ScaleLinear<number, number>>()

  const [activeCouncilMember, setActiveCouncilMember] =
    useState<CouncilMember>()
  const [squareRef, { width: wrapperWidth, height: wrapperHeight }] =
    useElementSize()
  const toolTipWrapperRef = useRef<HTMLDivElement>(null)
  const isMobile = wrapperWidth < 600
  const margin = { top: 30, right: 20, bottom: 30, left: 0 },
    width = wrapperWidth - margin.left - margin.right,
    height = wrapperHeight - margin.top - margin.bottom
  const circleRadius = 6
  const getValueFromCouncilMember = (member: CouncilMember) => {
    const value = member[categoryToDisplay]?.[1] ?? 0
    return +value
  }

  const setupChart = () => {
    const svg = d3.select(svgRef.current)
    setSvgSelection(svg)
    // redraw everything again
    svg.selectChildren().remove()
    // add histogram group
    const newHistogramGroupSelection = svg.append('g').attr('id', 'histogram')
    setHistogramGroupSelection(newHistogramGroupSelection)
    // set the dimensions and margins of the graph
    const newChartGroupSelection = svg
      .attr('width', width)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', 'translate(' + margin.left + ',' + 0 + ')')
    setChartGroupSelection(newChartGroupSelection)
    // add the x Axis
    const newXLinearScale = d3
      .scaleLinear()
      .domain(xScaleExtent)
      .range([0, width])
    setXLinearScale(() => newXLinearScale)
    const newXAxisSelection = newChartGroupSelection
      .append('g')
      .attr('id', 'xAxis')
      .attr('transform', 'translate(0,' + height + ')')
      .call(d3.axisBottom(newXLinearScale).tickSize(5).ticks(0)) //.select(".domain").remove()
    newXAxisSelection
      .append('image')
      .attr('xlink:href', leftArrow)
      .attr('width', 22)
      .attr('y', '.4em')
      .attr('x', 3)
    newXAxisSelection
      .append('text')
      .attr('class', 'xLabel')
      .attr('text-anchor', 'start')
      .attr('x', 30)
      .attr('y', '1.3em')
      .text('links')
    newXAxisSelection
      .append('image')
      .attr('xlink:href', rightArrow)
      .attr('text-anchor', 'end')
      .attr('x', width - 25)
      .attr('y', '.4em')
      .attr('width', 22)
    newXAxisSelection
      .append('text')
      .attr('class', 'xLabel')
      .attr('text-anchor', 'end')
      .attr('x', width - 30)
      .attr('y', '1.3em')
      .text('rechts')
    setXAxisSelection(newXAxisSelection)

    // add the y Axis
    const newYLinearScale = d3.scaleLinear().domain([0, 25]).range([height, 0])
    setYLinearScale(() => newYLinearScale)
    newChartGroupSelection.append('g').attr('class', 'histogram')
  }

  useEffect(() => {
    setupChart()
  }, [wrapperWidth])
  useEffect(() => {
    const newYLinearScale = d3.scaleLinear().domain([0, 25]).range([height, 0])
    setYLinearScale(() => newYLinearScale)
    const newXLinearScale = d3
      .scaleLinear()
      .domain(xScaleExtent)
      .range([0, width])
    setXLinearScale(() => newXLinearScale)
    // reposition the x axis
    xAxisSelection?.attr('transform', 'translate(0,' + height + ')')

    if (
      !svgSelection ||
      !newXLinearScale ||
      !newYLinearScale ||
      !chartGroupSelection
    )
      return
    // reposition the chart group
    chartGroupSelection.attr(
      'transform',
      'translate(' + margin.left + ',' + 0 + ')'
    )
    histogramGroupSelection && histogramGroupSelection.selectChildren().remove()
    histogramGroupSelection &&
      showSinglePoints &&
      appendHistogramm(
        data,
        categoryToDisplay,
        histogramGroupSelection,
        newXLinearScale,
        width,
        height,
        partyFilter,
        highlightCouncilMembers,
        setActiveCouncilMember,
        toolTipWrapperRef
      )
  }, [
    categoryToDisplay,
    wrapperWidth,
    wrapperHeight,
    partyFilter,
    highlightCouncilMembers,
    histogramGroupSelection,
    showSinglePoints,
    data,
  ])
  return (
    <ChartWrapper ref={squareRef}>
      <ToolTipWrapper ref={toolTipWrapperRef}>
        <ToolTip activeData={activeCouncilMember} />
      </ToolTipWrapper>
      <ChartSVG ref={svgRef}></ChartSVG>
    </ChartWrapper>
  )
}
export default MainChart
const ToolTipWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 100;
  pointer-events: none;
`
const ChartWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  height: 100%;
  font-family: var(--font-sans-serif);
  max-width: 1200px;
  margin: 0 auto;
`
const ChartSVG = styled.svg`
  box-sizing: border-box;
  overflow: visible;
  /* outline: solid 1px red; */
  /* overflow: hidden; */
  /* padding: 3em 15px; */
  path {
    stroke-dasharray: 2200;
    stroke-dashoffset: 2200;
    animation: dash 1s ease-in-out forwards;
  }
  .trustIntervallLine {
    animation: dash 0.9s ease-in-out forwards;
    stroke: currentColor;
    pointer-events: none;
  }
  .annotationLine {
    animation: fadeIn ease-in-out 2s;
    stroke: currentColor;
    pointer-events: none;
  }
  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
  .xLabel {
    font: var(--font-sans-serif);
    fill: var(--text-color);
  }
  .councilMemberCircle {
    stroke: var(--site-background);
    stroke-width: 1px;
    &:hover {
      stroke: currentColor;
      stroke-width: 15px;
      // smooth shadow
      filter: drop-shadow(3px 5px 2px rgb(0 0 0 / 0.4));
    }
    transition: all 0.3s ease-in-out;
  }
  .activeCouncilMemberCircle {
    animation: dash 0.7s ease-in-out forwards;
    stroke: currentColor;
    stroke-width: 15px;
    filter: drop-shadow(3px 5px 2px rgb(0 0 0 / 0.4));
  }
  @keyframes dash {
    from {
      stroke-width: 0;
    }
    to {
      stroke-dashoffset: 0;
    }
  }
  #xAxis {
    opacity: 1;
    text {
      font-size: 1.5rem !important;
      font-weight: 600;
    }
  }
`
